import { useEffect, useState } from "react";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Bell, BellRing, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import {
  isPushSupported,
  isPushSubscribed,
  subscribeToPush,
} from "../utils/pushNotifications";

/**
 * PushSubscribeNudge — gentle, dismissible card that asks the user to enable
 * browser push notifications. The notification cron has been running for ages
 * but nobody's subscribed — without this nudge, all those daily/streak
 * reminders go to nobody.
 *
 * Visibility rules:
 *  1. Hidden if the browser doesn't support push (Safari iOS PWA only does so
 *     after the user installs to home screen, otherwise no point asking).
 *  2. Hidden if already subscribed.
 *  3. Hidden if dismissed within the last 7 days (`ll_push_nudge_dismissed_at`).
 *  4. Hidden until the app has been open for ~6s (avoid login flicker; let the
 *     dashboard stabilize first).
 *
 * Tapping "Turn on reminders" opens the native permission dialog and POSTs the
 * subscription to /api/notifications/subscribe.
 */

const STORAGE_DISMISSED = "ll_push_nudge_dismissed_at";
const DISMISS_DAYS = 7;

const PushSubscribeNudge = () => {
  const { token } = useAuth();
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) return;

    // Browser support gate
    if (!isPushSupported()) return;

    let alive = true;

    const check = async () => {
      try {
        const already = await isPushSubscribed();
        if (!alive || already) return;

        // Dismissal cooldown
        let dismissedAt = null;
        try {
          dismissedAt = localStorage.getItem(STORAGE_DISMISSED);
        } catch {
          /* localStorage blocked → keep going */
        }
        if (dismissedAt) {
          const elapsed = Date.now() - parseInt(dismissedAt, 10);
          if (elapsed < DISMISS_DAYS * 24 * 60 * 60 * 1000) return;
        }

        // Settle delay
        setTimeout(() => alive && setShow(true), 6000);
      } catch (err) {
        console.debug("push nudge check failed", err);
      }
    };

    check();

    return () => {
      alive = false;
    };
  }, [token]);

  const handleEnable = async () => {
    setBusy(true);
    try {
      await subscribeToPush(token);
      toast.success("Reminders are on 🔔 — we'll keep them gentle.");
      setShow(false);
      try {
        // Clear dismissal so the card stays gone permanently (the
        // isPushSubscribed check will handle visibility going forward).
        localStorage.removeItem(STORAGE_DISMISSED);
      } catch {
        /* ignore */
      }
    } catch (err) {
      const msg = err?.message || "Couldn't enable reminders.";
      if (msg.includes("denied")) {
        toast.error("Notification permission was denied. You can re-enable in your browser settings.");
        // Treat as dismissal so we don't keep nagging.
        try { localStorage.setItem(STORAGE_DISMISSED, String(Date.now())); } catch { /* ignore */ }
        setShow(false);
      } else {
        toast.error(msg);
      }
    } finally {
      setBusy(false);
    }
  };

  const handleDismiss = () => {
    setShow(false);
    try {
      localStorage.setItem(STORAGE_DISMISSED, String(Date.now()));
    } catch {
      /* ignore */
    }
  };

  if (!show) return null;

  return (
    <Card
      className="relative overflow-hidden border-amber-400/30 bg-gradient-to-br from-amber-500/10 via-orange-500/8 to-pink-500/10"
      data-testid="push-subscribe-nudge"
    >
      <button
        onClick={handleDismiss}
        aria-label="Dismiss reminders nudge"
        className="absolute top-2 right-2 h-7 w-7 flex items-center justify-center rounded-full hover:bg-background/50 transition-colors"
        data-testid="push-nudge-dismiss"
      >
        <X className="h-3.5 w-3.5 text-muted-foreground" />
      </button>
      <div className="p-4 pr-10 flex items-start gap-3">
        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
          <BellRing className="h-5 w-5 text-amber-600 dark:text-amber-400" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-primary mb-1">
            Turn on gentle reminders
          </p>
          <p className="text-[12px] text-muted-foreground mb-3 leading-relaxed">
            Daily affirmations, streak nudges, full-moon whispers — only when it
            matters. No spam, you can turn them off anytime.
          </p>
          <div className="flex gap-2">
            <Button
              size="sm"
              disabled={busy}
              onClick={handleEnable}
              className="rounded-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white border-0 h-8 px-4 text-xs"
              data-testid="push-nudge-enable-btn"
            >
              <Bell className="h-3 w-3 mr-1.5" />
              {busy ? "Enabling…" : "Turn on reminders"}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={handleDismiss}
              className="rounded-full h-8 px-3 text-xs text-muted-foreground hover:text-foreground"
              data-testid="push-nudge-not-now-btn"
            >
              Not now
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default PushSubscribeNudge;
